'use strict'

const { visibleFieldDescription } = require('./form-field-description')

const REQUIRED_TYPES = new Set([
  'text',
  'number',
  'dateTime',
  'richText',
  'multiselect',
  'address',
  'password',
  'image',
  'file',
  'signature',
  'duration'
])

const DEFAULT_TYPES = new Set([
  'text',
  'number',
  'dateTime',
  'richText',
  'multiselect',
  'address'
])

const FORM_FIELD_TYPES = new Set([
  'formInput',
  'formTextArea',
  'formCheckBox',
  'formFile',
  'formPassword',
  'formRichText',
  'formMultiSelect',
  'formSignature',
  'formDuration',
  'formDate'
])

function columnSupportsRequired (column) {

  return !!(column && REQUIRED_TYPES.has(column.type))
}

function columnSupportsDefault (column) {

  return !!(column && DEFAULT_TYPES.has(column.type))
}

function isEmptyColumnValue (value, column) {

  if (value == null) return true

  if (typeof value === 'string') {

    const text = value.trim()

    if (!text) return true

    if (column && column.type === 'multiselect' && text === '[]') return true

    return false
  }

  if (typeof value === 'number') return !Number.isFinite(value)

  if (Array.isArray(value)) return value.length === 0

  if (typeof Buffer !== 'undefined' && Buffer.isBuffer(value)) return value.length === 0

  if (typeof File !== 'undefined' && value instanceof File) return value.size === 0

  if (typeof Blob !== 'undefined' && value instanceof Blob) return value.size === 0

  if (typeof value === 'object') {

    if (value.start && value.end) return false
    if (typeof value.url === 'string' && value.url.trim()) return false
    if (value.data == null || value.data === '') return true

    return false
  }

  return false
}

function coerceDefault (column, raw) {

  if (raw == null) return undefined

  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : undefined

  const text = String(raw).trim()

  if (!text) return undefined

  if (column && column.type === 'number') {

    const number = Number(text)

    return Number.isFinite(number) ? number : undefined
  }

  return text
}

function staticDefaultValue (column) {

  if (!columnSupportsDefault(column)) return undefined

  return coerceDefault(column, column.defaultValue)
}

function applyColumnDefaults (columns, values) {

  if (!values) return values

  for (const column of columns || []) {

    if (!column || !column.uniqueTitle) continue

    const fill = staticDefaultValue(column)

    if (fill === undefined) continue

    if (Object.prototype.hasOwnProperty.call(values, column.uniqueTitle) && !isEmptyColumnValue(values[column.uniqueTitle], column)) continue

    values[column.uniqueTitle] = fill
  }

  return values
}

function missingRequiredColumns (columns, values, options) {

  const partial = !!(options && options.partial)
  const missing = []

  for (const column of columns || []) {

    if (!column || !column.uniqueTitle || !column.required || !columnSupportsRequired(column)) continue

    const present = !!(values && Object.prototype.hasOwnProperty.call(values, column.uniqueTitle))

    if (partial && !present) continue

    if (isEmptyColumnValue(values && values[column.uniqueTitle], column)) missing.push(column)
  }

  return missing
}

function requiredValueMessage (columns) {

  const labels = (columns || []).map((column) => column.title || column.uniqueTitle)

  if (labels.length === 1) return `${labels[0]} is required`

  return `Required values missing: ${labels.join(', ')}`
}

function storedColumnWrite (type, input) {

  const source = input || {}
  const column = { type }
  const description = typeof source.description === 'string' ? source.description.trim() : ''
  const fill = columnSupportsDefault(column) ? coerceDefault(column, source.defaultValue) : undefined

  return {
    required: columnSupportsRequired(column) && !!source.required,
    description,
    defaultValue: fill == null ? '' : fill
  }
}

function columnWriteFromBody (body) {

  if (!body || typeof body !== 'object') return null

  if (!Object.prototype.hasOwnProperty.call(body, 'required') &&
      !Object.prototype.hasOwnProperty.call(body, 'description') &&
      !Object.prototype.hasOwnProperty.call(body, 'defaultValue') &&
      !Object.prototype.hasOwnProperty.call(body, 'validationRules') &&
      !Object.prototype.hasOwnProperty.call(body, 'valueRules')) {

    return null
  }

  const rules = {
    required: body.required,
    description: body.description,
    defaultValue: body.defaultValue
  }

  if (Object.prototype.hasOwnProperty.call(body, 'validationRules')) rules.validationRules = body.validationRules
  if (Object.prototype.hasOwnProperty.call(body, 'valueRules')) rules.valueRules = body.valueRules

  return rules
}

function fieldDescription (columnDescription, fieldDescriptionText) {

  const own = visibleFieldDescription(fieldDescriptionText)

  if (own) return own

  return visibleFieldDescription(columnDescription)
}

function applyColumnToField (field, column) {

  if (!field || !column) return field

  field.required = columnSupportsRequired(column) && !!column.required

  const description = fieldDescription(column.description, field.description)

  if (description) field.description = description
  else if (!visibleFieldDescription(field.description)) field.description = ''

  const fill = staticDefaultValue(column)

  if (fill === undefined) delete field.defaultValue
  else field.defaultValue = fill

  field.columnType = column.type || ''

  if (column.validationRules) field.validationRules = column.validationRules
  else delete field.validationRules

  return field
}

function visitFormFields (components, visit) {

  for (const component of components || []) {

    if (!component || typeof component !== 'object') continue

    if (FORM_FIELD_TYPES.has(component.type) && component.uniqueTitle) visit(component)

    if (Array.isArray(component.components)) visitFormFields(component.components, visit)
  }
}

function missingRequiredFromFields (components, values, options) {

  const skipSecrets = !!(options && options.skipUnchangedSecrets)
  const columns = []

  visitFormFields(components, (field) => {

    if (!field.required) return

    if (skipSecrets && (field.type === 'formPassword' || field.type === 'formFile')) return

    columns.push({
      uniqueTitle: field.uniqueTitle,
      title: field.title || field.label || field.uniqueTitle,
      type: field.type === 'formMultiSelect' ? 'multiselect' : 'text',
      required: true
    })
  })

  return missingRequiredColumns(columns, values)
}

function includeEmptyRequiredValue (column) {

  if (!column || !column.required || !columnSupportsRequired(column)) return false

  if (column.type === 'password' || column.type === 'image' || column.type === 'file' || column.type === 'signature') return false

  return true
}

module.exports = {
  columnSupportsRequired,
  columnSupportsDefault,
  isEmptyColumnValue,
  staticDefaultValue,
  applyColumnDefaults,
  missingRequiredColumns,
  requiredValueMessage,
  storedColumnWrite,
  columnWriteFromBody,
  fieldDescription,
  applyColumnToField,
  visitFormFields,
  missingRequiredFromFields,
  includeEmptyRequiredValue
}
